/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useRef, useState } from "react";

import cn from "classnames";

import {
  ListOptionType,
  SelectListInterface,
  SelectListSettingsInterface,
  TObject,
} from "constants/interfaces";
import cloneDeep from "utils/cloneDeep";
import isObject from "utils/isObject";
import { useDetectClickOutside } from "utils/useDetectClickOutside";

import { throttle } from "hooks/useThrottle";

import { SkeletonDiv } from "components/common";
import { ArrowDown } from "components/icons/common";
import { FilteredMenu } from "components/ui/DropDown/FilteredMenu";

import { Input } from "../Input";
import Label from "../Label";
import { Spinner } from "../Loaders";
import SelectCard from "../MultiSelectSearch/SelectCard";
import Stack from "../Stack";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";
import { getAvatar } from "models/User";

export const getItemKey = (item: TObject) =>
  isObject(item) ? item.key || item.uid || item.id : null;

export const findSelectListTarget = (
  object: {
    [key: string]: ListOptionType;
  },
  key: ListOptionType["id"],
) => {
  let target, targetKey;

  const values = Object.values(object);
  for (const element of values) {
    const value = element;
    if (value.key === key) {
      target = value;
      targetKey = value.key;
      break;
    }

    if (value.id === key) {
      target = value;
      targetKey = value.id;
      break;
    }

    if (value.uid === key) {
      target = value;
      targetKey = value.uid;
      break;
    }
  }

  if (!target) {
    target = object[key];
    targetKey = key;
  }

  return {
    ...target,
    targetKey,
  };
};

interface ISettings extends SelectListSettingsInterface {
  isMulti?: boolean;
  shouldInvertSelects?: boolean;
  shouldInvertItems?: boolean;
  id?: string;
}

interface IMultiSelectList
  extends Omit<SelectListInterface, "onChange" | "selectedOption"> {
  selectedOptions: ListOptionType[];
  onChange?: (selectedItems: ListOptionType[]) => void;
  SelectedItemsSlot?: (
    selectedItems: ListOptionType[],
    onDeselect: (item: ListOptionType) => any,
  ) => JSX.Element;
  OptionItemsSlot?: (
    items: ListOptionType[],
    onSelect: (item: ListOptionType) => any,
  ) => JSX.Element;
  setSearchParams?: (value: string) => void;
  searchParams?: string;
  isLoading?: boolean;
}

const MultiSelectList = ({
  id,
  options: optionsFromProps,
  selectedOptions: selectedOptionsFromProps,
  onSelect: checkIfCanSelect,
  onRemove,
  onChange,
  disabled = false,
  onScrollEnd,
  isFetchingMore,
  topSlot,
  SelectedItemsSlot,
  OptionItemsSlot,
  setSearchParams,
  searchParams,
  isLoading,

  ...args
}: IMultiSelectList & ISettings) => {
  const placeholder_small = useEnvs((state) => state.placeholder_small);

  const settings: ISettings = {
    position: "bottom",
    isMulti: true,
    shouldInvertSelects: true,
    shouldInvertItems: false,
    inputProps: {},
    labelProps: {
      size: "large",
    },
    label: "Select",
    itemProps: {
      mediaSize: "lg",
      mediaVariant: "circle",
      mediaFit: "cover",
    },
    cardAttachment: undefined,
  };

  if (args) {
    Object.assign(settings, {
      ...args,
    });

    if (args.hasOwnProperty("givenItems")) {
      // @ts-expect-error
      optionsFromProps = args.givenItems;
    }

    if (args.hasOwnProperty("defaultItems")) {
      // @ts-expect-error
      selectedOptionsFromProps = args.defaultItems;
    }
  }

  const {
    position,
    isMulti,
    shouldInvertSelects,
    shouldInvertItems,
    inputProps,
    labelProps,
    label,
    itemProps,
    cardAttachment,
  } = settings;

  const [options, setOptions] = useState<ListOptionType[]>([]);
  const [selectedItems, setSelectedItems] = useState<ListOptionType[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useDetectClickOutside(boxRef, () => setIsOpen(false));

  useEffect(() => {
    const optionsObjectById: TObject = {};
    if (optionsFromProps && optionsFromProps.length > 0) {
      optionsFromProps.forEach((item) => {
        if (!item) return;

        const key = getItemKey(item);
        if (!item.hasOwnProperty("selected")) {
          item.selected = false;
        }

        optionsObjectById[key] = cloneDeep({ ...item, key });

        if (shouldInvertItems) {
          optionsObjectById[key].selected = !optionsObjectById[key].selected;
        }
      });
    } else {
      // #WARNING: This is the case when the options are updated and default items are old. We need to reset the options and selected items.
      setOptions([]);
    }

    const newSelectedItemsById: TObject = {};
    selectedItems.forEach((item: ListOptionType | string | number) => {
      if (!item) return;
      let id;
      if (
        (typeof item === "string" && item.length > 0) ||
        typeof item === "number"
      ) {
        id = item;
      }

      if (typeof item === "object") {
        id = getItemKey(item);
      }

      if (!id) return;
      const target = findSelectListTarget(optionsObjectById, id);
      // @ts-expect-error
      if ("selected" in item && item.selected) {
        target.selected = item.selected;
      }

      newSelectedItemsById[id] = target;
    });

    if (selectedOptionsFromProps && selectedOptionsFromProps.length > 0) {
      selectedOptionsFromProps.forEach((item) => {
        if (!item) return;
        let id = item?.id;
        if (typeof item === "string" || typeof item === "number") {
          id = item;
        }

        const key = getItemKey(item) || id;
        if (!key) return;

        const target = findSelectListTarget(optionsObjectById, key);
        if (target.hasOwnProperty("name") && target.hasOwnProperty("id")) {
          if (shouldInvertSelects) {
            target.selected = !target.selected;
          } else {
            target.selected = true;
          }

          const { targetKey } = target;
          newSelectedItemsById[targetKey!] = { ...target, key };
          delete optionsObjectById[key];
        }
      });

      setSelectedItems(Object.values(newSelectedItemsById));
    } else {
      setSelectedItems([]);
    }

    setOptions(Object.values(optionsObjectById));
  }, [optionsFromProps, selectedOptionsFromProps]);

  const onSelect = (item: ListOptionType) => {
    if (checkIfCanSelect) {
      const shouldContinue = checkIfCanSelect(item);
      if (shouldContinue === false) return;
    }

    item.selected = !item.selected;

    setSelectedItems((prev) => {
      const newSelectedItems = [...prev, item];
      if (onChange) onChange(newSelectedItems);
      return newSelectedItems;
    });

    const remainingItemsLength = options.length - 1;
    if (remainingItemsLength === 0 || !isMulti) setIsOpen(false);
  };

  const onDeselect = (item: ListOptionType) => {
    if (onRemove) onRemove(item);
    item.selected = !item.selected;
    const remainingSelectedItems = selectedItems.filter(
      (el) => el.key !== item.key || el.uid !== item.uid || el.id !== item.id,
    );

    setSelectedItems(remainingSelectedItems);
    setOptions([...options, item]);
    if (onChange) onChange(remainingSelectedItems);
  };

  let isMultiDisabled = false;
  if (!isMulti && selectedItems.length > 0) {
    isMultiDisabled = true;
  }

  const menuRef = useRef<HTMLUListElement>(null);

  const loadMore = () => {
    const elementTarget = menuRef.current;
    if (!elementTarget) return;
    const hasScrollReachedBottom =
      elementTarget.scrollTop + elementTarget.offsetHeight >=
      elementTarget.scrollHeight;

    if (hasScrollReachedBottom && !isFetchingMore && onScrollEnd) {
      onScrollEnd();
    }
  };

  useEffect(() => {
    if (onScrollEnd) {
      const throttledLoadMore = throttle(loadMore, 500, isFetchingMore);
      menuRef.current?.addEventListener("scroll", throttledLoadMore);
      return () => {
        menuRef.current?.removeEventListener("scroll", throttledLoadMore);
      };
    }
  }, []);

  return (
    <div className="w-full" test-id="i7Erghnq">
      {label && (
        <Label
          htmlFor="multi-select-list"
          note={
            isMultiDisabled && !isMulti
              ? "To choose again, you must remove the selection first."
              : ""
          }
          {...labelProps}
          test-id="z_3ODnND"
        >
          {label}
        </Label>
      )}

      <div ref={boxRef}>
        <div
          className={cn("relative w-full", {
            "mt-1": label,
          })}
          onClick={() => {
            if (disabled || isMultiDisabled || isOpen) return;
            setIsOpen(true);
          }}
          test-id="wwEIij97"
        >
          {searchParams !== undefined ? (
            <Input
              id="dynamic-search"
              disabled={disabled || isMultiDisabled}
              type="text"
              value={searchParams}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length === 0) setIsOpen(false);
                setIsOpen(true);

                if (setSearchParams) {
                  setSearchParams(value);
                }
              }}
              onFocus={() => setIsOpen(true)}
              {...inputProps}
            />
          ) : (
            <Input
              id={id ?? "multi-select-list"}
              disabled={disabled || isMultiDisabled}
              type="text"
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length === 0) setIsOpen(false);
                setIsOpen(true);
                setSearchValue(value);
              }}
              onFocus={() => setIsOpen(true)}
              {...inputProps}
              test-id="qJMv5xP6"
            />
          )}

          {(isLoading || isFetchingMore) && (
            <div
              className="absolute right-10 top-3 text-white"
              test-id="VDN1wzus"
            >
              <Spinner size="sm" test-id="S3bJ7el-" />
            </div>
          )}

          <div
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer transition-transform",
              { "rotate-180": isOpen },
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (disabled || isMultiDisabled) return;
              setIsOpen((prev) => !prev);
            }}
            test-id="06AW0b1S"
          >
            <ArrowDown test-id="BtTcnfkc" />
          </div>
        </div>

        <Stack
          className={cn({
            relative: position === "top",
          })}
          test-id="rOqHMFj3"
        >
          <ul
            ref={menuRef}
            style={{
              maxWidth: boxRef.current?.offsetWidth,
            }}
            className={cn(
              "flex flex-col w-full list-none",
              "bg-primary  transition-all duration-200 absolute z-[1]",
              position === "top" && "bottom-[45px]",
              position === "bottom" && "border-t-0",
              isOpen
                ? "max-h-64 overflow-auto border-2 "
                : "max-h-0 border-0  overflow-hidden",
              SelectedItemsSlot
                ? "border-none divide-y divide-c-gray-600"
                : "border-tertiary-accent divide-y-2 divide-tertiary",
            )}
            test-id="dHUMXwsS"
          >
            {topSlot}

            <FilteredMenu
              list={options}
              filter={searchValue}
              test-id="So8WX2Wy"
            >
              {(opts: ListOptionType[]) =>
                OptionItemsSlot && typeof OptionItemsSlot === "function" ? (
                  OptionItemsSlot(opts, onSelect)
                ) : opts.length > 0 ? (
                  opts.map((opt, index) => (
                    <SelectCard
                      key={opt.key || opt.id || index}
                      as="li"
                      media={{
                        src: getAvatar(opt) || placeholder_small,
                        size: itemProps!.mediaSize,
                        variant: itemProps!.mediaVariant,
                        fit: itemProps!.mediaFit,
                      }}
                      title={opt.name}
                      isSelected={opt.selected}
                      disabled={disabled || isMultiDisabled}
                      onClick={() =>
                        !disabled && !isMultiDisabled && onSelect(opt)
                      }
                      className="bg-primary h-16 py-2 px-4"
                      test-id="rZxe0g_D"
                    />
                  ))
                ) : (
                  <div>
                    <p className="text-center text-white py-4">
                      No results found
                    </p>
                  </div>
                )
              }
            </FilteredMenu>
          </ul>
        </Stack>

        <Stack as="ul" test-id="H7RavQOA">
          {SelectedItemsSlot && typeof SelectedItemsSlot === "function"
            ? SelectedItemsSlot(selectedItems, onDeselect)
            : selectedItems.map((item, index) => (
                <li
                  key={item.key ?? item.id ?? index}
                  className="pt-1 flex w-full"
                  test-id="R2Xr5k3w"
                >
                  {cardAttachment && (
                    <div className="bg-primary w-10 h-14" test-id="uYbWLZYa">
                      {cardAttachment(index, item)}
                    </div>
                  )}

                  <SelectCard
                    as="div"
                    media={{
                      src: getAvatar(item) || placeholder_small,
                      size: itemProps!.mediaSize,
                      variant: itemProps!.mediaVariant,
                      fit: itemProps!.mediaFit,
                    }}
                    title={item.name}
                    isSelected={item.selected}
                    disabled={disabled}
                    onClick={() => onDeselect(item)}
                    className={cn(
                      "bg-secondary w-full",
                      itemProps!.mediaSize === "lg" ? "px-5" : "pl-3 pr-5",
                    )}
                    test-id="83YHET7x"
                  />
                </li>
              ))}
        </Stack>
      </div>
    </div>
  );
};

const MenuLoader = ({
  label,
  labelProps,
  loader,
}: {
  label?: string;
  labelProps: any;
  loader?: any;
}) => {
  return (
    <Stack test-id="eZWuDH7j">
      {label && (
        <Label
          size={labelProps?.size || "large"}
          {...labelProps}
          test-id="ndvVF9IZ"
        >
          {label}
        </Label>
      )}

      {loader || <SkeletonDiv test-id="0vT3wPMf" />}
    </Stack>
  );
};

const SugarMultiSelectList = (props: IMultiSelectList & ISettings) => {
  const { loader, isLoading, ...rest } = props;
  if (isLoading) {
    return (
      <MenuLoader
        label={rest.label}
        labelProps={rest.labelProps}
        loader={loader}
        test-id="8ayJITdg"
      />
    );
  }

  return <MultiSelectList {...rest} test-id="6SwFXbi3" />;
};

export default SugarMultiSelectList;
